import React from 'react';
import styles from '../irlObservations2026.module.css';
import { DueDateCell, FilterChip, FreshnessBadge, PriorityMark, SearchField, StatusBadge, isRequestOverdue, requestStatusLabel } from '../chrome';
import { BulkEditBar, ListCheckbox, ListKebabMenu, ListNotice } from '../listChrome';
import { filledUpdates, foldersForBulkWorkstream, toggleRowSelection, visibleSelectionState } from '../listActions';
import { IRL_STATUS_FILTERS } from '../dummyData';
import { AiReviewReadyBanner, stripHtmlText } from './shared';

export default function IRL_Main({
    requests,
    status,
    onStatus,
    workstream,
    onWorkstream,
    priority,
    onPriority,
    query,
    onQuery,
    onOpen,
    onCreate,
    onAiReview,
    onReviewSuggestions,
    menuOpen,
    onMenu,
    workstreams,
    members,
    folders,
    selectedIds,
    onSelectedIds,
    bulkDraft,
    onBulkDraft,
    onBulkApply,
    onBulkDelete,
    onExportCsv,
    onExportPdf,
    onStockQuestions,
    notice,
    busy,
}) {
    const visibleIds = requests.map(item => item.id);
    const selection = visibleSelectionState(visibleIds, selectedIds);
    const selectedCount = (selectedIds || []).length;
    const showTagColumn = (requests || []).some(item => item.tag != null && String(item.tag) !== '');

    const toggleVisible = () => {
        const selected = new Set((selectedIds || []).map(id => String(id)));
        if (selection.allVisibleSelected) {
            visibleIds.forEach(id => selected.delete(String(id)));
        } else {
            visibleIds.forEach(id => selected.add(String(id)));
        }
        onSelectedIds(Array.from(selected));
    };

    return (
        <>
            <div className={styles.contentHeader}>
                <div>
                    <h2 className={styles.contentTitle}>Information Requests</h2>
                    <p className={styles.contentSubtitle}>Track project questions, responses, evidence and follow-ups.</p>
                </div>
                <div className={styles.headerActions}>
                    {onReviewSuggestions && (
                        <button type="button" className={styles.ghostBtn} onClick={onReviewSuggestions}>
                            Review suggestions
                        </button>
                    )}
                    <button type="button" className={styles.ghostBtn} onClick={onAiReview}>
                        AI Review
                    </button>
                    <button type="button" className={styles.ghostBtn} onClick={onStockQuestions}>
                        Stock Questions
                    </button>
                    <button type="button" className={`${styles.primaryBtn} add-irl-button`} onClick={onCreate}>
                        + New request
                    </button>
                    <ListKebabMenu
                        open={menuOpen}
                        onToggle={onMenu}
                        onExportCsv={onExportCsv}
                        onExportPdf={onExportPdf}
                        disabled={busy}
                    />
                </div>
            </div>
            {onReviewSuggestions && <AiReviewReadyBanner onReview={onReviewSuggestions} />}
            <div className={styles.filterRow}>
                <SearchField value={query} onChange={onQuery} placeholder="Search requests…" />
                {IRL_STATUS_FILTERS.map(item => (
                    <FilterChip key={item} label={item} active={status === item} onClick={() => onStatus(item)} />
                ))}
                <select className={styles.select} value={workstream} onChange={event => onWorkstream(event.target.value)}>
                    <option value="all">All workstreams</option>
                    {(workstreams || []).map(ws => (
                        <option key={ws.id} value={String(ws.id)}>
                            {ws.name}
                        </option>
                    ))}
                </select>
                <select className={styles.select} value={priority} onChange={event => onPriority(event.target.value)}>
                    <option value="all">Priority</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                </select>
            </div>
            <ListNotice message={notice} />
            <BulkEditBar
                count={selectedCount}
                busy={busy}
                applyDisabled={!Object.keys(filledUpdates(bulkDraft || {})).length}
                onApply={onBulkApply}
                onDelete={onBulkDelete}
                onClear={() => onSelectedIds([])}
                deleteSingular="information request"
                deletePlural="information requests"
                fields={[
                    {
                        key: 'owner',
                        label: 'Assigned to',
                        value: bulkDraft?.owner || '',
                        onChange: value => onBulkDraft({ owner: value }),
                        options: (members || []).map(member => ({ id: member.id, label: member.name })),
                    },
                    {
                        key: 'workstream',
                        label: 'Workstream',
                        value: bulkDraft?.workstream || '',
                        onChange: value => onBulkDraft({ workstream: value, folder: '' }),
                        options: (workstreams || []).map(ws => ({ id: ws.id, label: ws.name })),
                    },
                    {
                        key: 'folder',
                        label: 'Folder',
                        value: bulkDraft?.folder || '',
                        onChange: value => onBulkDraft({ folder: value }),
                        disabled: !bulkDraft?.workstream && !(folders || []).length,
                        options: foldersForBulkWorkstream(folders, bulkDraft?.workstream).map(folder => ({
                            id: folder.id,
                            label: folder.path || folder.name,
                        })),
                    },
                    {
                        key: 'due',
                        label: 'Deadline',
                        type: 'date',
                        value: bulkDraft?.due || '',
                        onChange: value => onBulkDraft({ due: value }),
                    },
                    {
                        key: 'priority',
                        label: 'Priority',
                        value: bulkDraft?.priority || '',
                        onChange: value => onBulkDraft({ priority: value }),
                        options: [
                            { id: 'High', label: 'High' },
                            { id: 'Medium', label: 'Medium' },
                            { id: 'Low', label: 'Low' },
                        ],
                    },
                ]}
            />
            <div className={`${styles.tableWrap} table-step`}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.checkCol}>
                                <ListCheckbox
                                    appearance="header"
                                    checked={selection.allVisibleSelected}
                                    indeterminate={selection.someVisibleSelected}
                                    onChange={toggleVisible}
                                    label="Select all visible requests"
                                />
                            </th>
                            <th>Request</th>
                            <th>Workstream</th>
                            {showTagColumn ? <th>Tag</th> : null}
                            <th>Priority</th>
                            <th>Owner</th>
                            <th>Due</th>
                            <th>Status</th>
                            <th>Freshness</th>
                            <th>Evidence</th>
                            <th>Comments</th>
                        </tr>
                    </thead>
                    <tbody>
                        {requests.map(item => {
                            const checked = (selectedIds || []).includes(item.id);
                            return (
                                <tr key={item.id}>
                                    <td className={styles.checkCol}>
                                        <ListCheckbox
                                            checked={checked}
                                            onChange={() => onSelectedIds(toggleRowSelection(selectedIds, item.id))}
                                            label={`Select ${item.id}`}
                                        />
                                    </td>
                                    <td>
                                        <button type="button" className={styles.rowBtn} onClick={() => onOpen(item.id)}>
                                            <span className={styles.rowId}>
                                                {item.id}
                                                {item.followUpOf ? `  Follow-up to ${item.followUpOf}` : ''}
                                            </span>
                                            <span className={styles.rowTitle}>{stripHtmlText(item.title)}</span>
                                        </button>
                                    </td>
                                    <td className={styles.wsMuted}>{item.workstream}</td>
                                    {showTagColumn ? <td className={styles.wsMuted}>{stripHtmlText(item.tag || '')}</td> : null}
                                    <td>
                                        <PriorityMark value={item.priority} />
                                    </td>
                                    <td>{item.owner}</td>
                                    <td>
                                        <DueDateCell due={item.due} overdue={isRequestOverdue(item)} />
                                    </td>
                                    <td>
                                        <StatusBadge status={requestStatusLabel(item)} />
                                    </td>
                                    <td>
                                        <FreshnessBadge value={item.freshness} />
                                    </td>
                                    <td className={styles.evidenceMeta}>{item.evidenceCount}</td>
                                    <td className={styles.evidenceMeta}>
                                        {item.comments}
                                        {item.newComments ? ` · ${item.newComments} new` : ''}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}
